/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { EMPTY, hasElements } from '../extensions/StringExtensions';
import { ProxyError } from './ProxyError';

export class ErrorTypeEnum {
    public static SERVER_ERROR: string = 'string_29002';
    public static SERVICE_UNAVAILABLE: string = 'string_29278';
    public static SERVER_TIMEOUT: string = 'string_29279';
    public static RETAIL_SERVER_REDIRECT_ERROR = 'RETAIL_SERVER_REDIRECT_ERROR';
    public static GENERICERRORMESSAGE: string = 'string_29201';
    public static MICROSOFT_DYNAMICS_SERVER_INTERNAL_ERROR: string = ErrorTypeEnum.GENERICERRORMESSAGE;
    public static BAD_REQUEST: string = 'string_29274';
    public static NOT_AUTHORIZIED: string = 'string_29275';
    public static FORBIDDEN: string = 'string_29276';
    public static PRECONDITION_FAILED: string = 'string_29277';
    public static RETURN_NO_ORDERS_FOUND: string = 'string_1218';
}

/**
 * Forward links for device activation errors.
 */
export class DeviceActivationErrorsForwardLinks {
    public static MICROSOFT_DYNAMICS_COMMERCE_RUNTIME_CHANNELDATABASECONNECTIONFAILED: string =
        'http://go.microsoft.com/fwlink/?LinkId=403590';
    public static MICROSOFT_DYNAMICS_COMMERCE_RUNTIME_REALTIMESERVICECONNECTIONFAILED: string =
        'http://go.microsoft.com/fwlink/?LinkId=403591';
    public static MICROSOFT_DYNAMICS_COMMERCE_RUNTIME_TERMINALNOTASSIGNEDTOSTORE: string = 'http://go.microsoft.com/fwlink/?LinkId=403592';
    public static MICROSOFT_DYNAMICS_COMMERCE_RUNTIME_USERNOTASSIGNEDTOSTORE: string = 'http://go.microsoft.com/fwlink/?LinkId=403593';
    public static MICROSOFT_DYNAMICS_COMMERCE_RUNTIME_INCORRECTSTAFFIDORPASSWORD: string = 'http://go.microsoft.com/fwlink/?LinkId=403594';
    public static MICROSOFT_DYNAMICS_COMMERCE_RUNTIME_DEVICEALREADYACTIVATED: string = 'http://go.microsoft.com/fwlink/?LinkId=519136';
    public static SERVER_ERROR: string = 'http://go.microsoft.com/fwlink/?LinkId=519137';
}

export class ErrorHelper {
    private static AGGREGATED_ERROR_RESOUCEIDS: string[] = [
        'Microsoft_Dynamics_Commerce_Runtime_AggregateValidationError',
        'Microsoft_Dynamics_Commerce_Runtime_AggregateCommunicationError',
        'Microsoft_Dynamics_Commerce_Runtime_InvalidCartLinesAggregateError'
    ];

    public static MICROSOFT_DYNAMICS_SERVER_INTERNAL_ERROR: string = 'Microsoft_Dynamics_Internal_Server_Error';

    public static isAggregatedErrorResourceId(errorResourceId: string): boolean {
        return ErrorHelper.AGGREGATED_ERROR_RESOUCEIDS.indexOf(errorResourceId) !== -1;
    }

    /**
     * Map response status code to error.
     *
     * @param {any} Error returned by retail server call.
     */
    public static MapResponseStatusCodeToError(errorMessage: string, statusCode: number, err?: any): ProxyError {
        let errorCode = '';
        let canRetry = false;

        switch (statusCode) {
            case 400:
                errorCode = ErrorTypeEnum.BAD_REQUEST;
                break;
            case 401:
                errorCode = ErrorTypeEnum.NOT_AUTHORIZIED;
                break;
            case 403:
                errorCode = ErrorTypeEnum.FORBIDDEN;
                break;
            case 412:
                errorCode = ErrorTypeEnum.PRECONDITION_FAILED;
                break;
            case 503:
                errorCode = ErrorTypeEnum.SERVICE_UNAVAILABLE;
                canRetry = true;
                break;
            case 500:
            default:
                errorCode = ErrorTypeEnum.SERVER_ERROR;
                errorMessage = 'The server error format is not supported or it does not have enough information.';

                // Temporary until the fix for Bug 681427 goes in
                // If no orders were found, than an error did not occur
                if (err && err.response && err.response.body && err.response.requestUri) {
                    if (err.response.requestUri.indexOf('GetOrderByReceiptId') > -1) {
                        if (err.response.body.indexOf('No orders were found') > -1) {
                            errorMessage = ErrorTypeEnum.RETURN_NO_ORDERS_FOUND;
                        }
                    }
                }
        }

        if (errorMessage && errorMessage.toUpperCase() === 'TIMEOUT') {
            errorCode = ErrorTypeEnum.SERVER_TIMEOUT;
        }

        return new ProxyError(errorCode, errorMessage || EMPTY, EMPTY, canRetry, err);
    }

    /**
     * Checks whether all the provided errors are retryable. If no error code is provided or an error is undefined,
     * then the errors will be treated as not retryable.
     *
     * @return {boolean} True if the error codes are retryable, false if the error codes are not retryable or not defined
     */
    public static isRetryable(errors: ProxyError[]): boolean {
        const numErrors: number = !errors ? 0 : errors.length;
        let isRetryable: boolean = numErrors > 0;
        for (let i: number = 0; i < numErrors; i++) {
            if (errors[i]) {
                isRetryable = isRetryable && errors[i].CanRetry;
            } else {
                isRetryable = false;
            }
        }

        return isRetryable;
    }

    /**
     * Gets a string with all error codes.
     * @param {ProxyError[]} errors the error collection.
     * @return {string} a formated string containing all error codes.
     */
    public static getErrorResourceIds(errors: ProxyError[]): string {
        let result: string = '';

        if (hasElements(errors)) {
            const errorResourceIds: string[] = [];
            for (const error of errors) {
                errorResourceIds.push(error.ErrorCode);
            }

            result = errorResourceIds.join(', ');
        }

        return result;
    }

    /**
     * Gets a string with all error messages.
     * @param {ProxyError[]} errors the error collection.
     * @return {string} a formated string containing all error messages.
     */
    public static getErrorMessages(errors: ProxyError[]): string {
        let result: string = '';

        if (hasElements(errors)) {
            const errorResourceIds: string[] = [];
            for (const error of errors) {
                errorResourceIds.push(error.LocalizedErrorMessage);
            }

            result = errorResourceIds.join(', ');
        }

        return result;
    }
}
